/**
 * Голос браузера — строго ОДНИМ каналом (находка приёмки 12.08).
 *
 * ⭐ Что случилось. Владелец звонил из кабинета с телефона: разговор прошёл,
 * робота он слышал, робот слышал его — а в расшифровке не оказалось НИ ОДНОГО
 * его слова, зато слова собеседника стояли дважды. Дефект воспроизведён на
 * Firefox (он, как и телефон владельца, просит стерео) и доказан числами:
 * в записи обе дорожки совпали бит в бит (разница −91 дБ), и на обеих —
 * голос собеседника.
 *
 * Причина. Разделение ролей у нас держится на том, что движок пишет запись
 * двумя дорожками: слева — что канал слышит, справа — что говорит. Когда
 * браузер в своём предложении просит Opus СТЕРЕО (`stereo=1`), у канала
 * появляются собственные два канала звука, и запись достаётся им: движок
 * кладёт в файл стерео самого потока, а не «слышу/говорю». Микрофон
 * оператора в файл не попадает вовсе.
 *
 * Chromium просит моно и дефекта не даёт — поэтому прежние пробы были
 * зелёными, а живой звонок владельца ломался.
 *
 * Правка: в своём предложении явно просим моно (`stereo=0` для приёма и
 * `sprop-stereo=0` для передачи). Для разговора это ничего не отнимает —
 * телефонная линия и так моно; для нас это возвращает роли, ради которых
 * запись и разбирается на две дорожки.
 *
 * Второй рубеж — на движке (`opus.conf.xml`, `mono=1`): даже если завтра
 * подключится не наш клиент и попросит стерео, разделение ролей не рассыплется.
 */

/** Параметры Opus, которыми задаётся стерео. Свои значения ставим сами. */
const STEREO_KEYS = ['stereo', 'sprop-stereo'];

/**
 * Переписывает в SDP настройки Opus на моно.
 *
 * Строка `a=rtpmap:111 opus/48000/2` не трогается намеренно: по RFC 7587 в ней
 * ВСЕГДА стоит 2, а решает дело именно `a=fmtp`. Правка rtpmap ломает
 * согласование и не меняет числа каналов.
 */
export function forceMonoOpus(sdp: string): string {
  const eol = sdp.includes('\r\n') ? '\r\n' : '\n';
  const lines = sdp.split(/\r?\n/);

  const payloads = lines
    .map((line) => /^a=rtpmap:(\d+) opus\//i.exec(line)?.[1])
    .filter((pt): pt is string => Boolean(pt));

  if (payloads.length === 0) {
    return sdp;
  }

  for (const pt of payloads) {
    const prefix = `a=fmtp:${pt} `;
    const at = lines.findIndex((line) => line.startsWith(prefix));

    const params = at >= 0
      ? lines[at]
        .slice(prefix.length)
        .split(';')
        .map((param) => param.trim())
        .filter((param) => param !== '' && !STEREO_KEYS.includes(param.split('=')[0].trim().toLowerCase()))
      : [];

    params.push('stereo=0', 'sprop-stereo=0');

    const rewritten = prefix + params.join(';');

    if (at >= 0) {
      lines[at] = rewritten;
    } else {
      // Своей строки параметров у кодека может не быть — тогда она встаёт
      // сразу за описанием кодека, как того требует порядок SDP.
      const rtpmapAt = lines.findIndex((line) => new RegExp(`^a=rtpmap:${pt} opus/`, 'i').test(line));
      lines.splice(rtpmapAt + 1, 0, rewritten);
    }
  }

  return lines.join(eol);
}

/**
 * Правка предложения перед отправкой. Форма — та, которую ждёт sip.js
 * (`SessionDescriptionHandlerModifier`).
 */
export async function monoAudioModifier(
  description: RTCSessionDescriptionInit,
): Promise<RTCSessionDescriptionInit> {
  if (!description.sdp) {
    return description;
  }

  return { ...description, sdp: forceMonoOpus(description.sdp) };
}
